import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

function EditKlant() {
    const navigate = useNavigate();
    const { id } = useParams();
    const token = sessionStorage.getItem('jwtToken');

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [userStatus, setUserStatus] = useState(null);
    const [userRole, setUserRole] = useState(null);

    const normalizeFeatures = useCallback((features) => {
        if (!features) return {};

        if (typeof features === 'string') {
            try {
                return JSON.parse(features);
            } catch {
                return {};
            }
        }

        if (typeof features === 'object') return features;

        return {};
    }, []);

    const normalizeCustomer = useCallback((customer) => {
        const features = normalizeFeatures(customer?.features);

        return {
            name: customer?.name || '',
            address: customer?.address || '',
            phone: customer?.phone || '',
            email: customer?.email || '',
            contact_firstname: customer?.contact_firstname || '',
            contact_lastname: customer?.contact_lastname || '',
            contact_mobile: customer?.contact_mobile || '',
            is_single_user: customer?.is_single_user === true || customer?.is_single_user === 'true' || customer?.is_single_user === 1,
            features: {
                ...features,
                private_lessons: features.private_lessons === true || features.private_lessons === 'true'
            }
        };
    }, [normalizeFeatures]);

    const [values, setValues] = useState({
        name: '',
        address: '',
        phone: '',
        email: '',
        contact_firstname: '',
        contact_lastname: '',
        contact_mobile: '',
        is_single_user: false,
        features: {
            private_lessons: false
        }
    });

    const [emailExists, setEmailExists] = useState(false);

    const fetchUserLoginData = useCallback(async (email) => {
        if (!email) return;

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/auth/logindata/${email}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (response.status === 204) {
                setUserStatus('Geen waarde');
                setUserRole('Geen waarde');
            } else if (response.status === 200) {
                setUserStatus(response.data.status || 'Geen waarde');
                setUserRole(response.data.role || 'Geen waarde');
            } else {
                console.error('Unexpected response status:', response.status);
                setUserStatus('Error fetching data');
                setUserRole('Error fetching data');
            }
        } catch (error) {
            console.error('Error fetching user login data:', error);
            setUserStatus('Fout');
            setUserRole('Fout');
        }
    }, [token]);

    const fetchCustomer = useCallback(() => {
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/customer/getcustomer/${id}`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
            .then((response) => {
                const customer = normalizeCustomer(response.data.data || {});
                setValues(customer);
                setIsLoading(false);
                fetchUserLoginData(customer.email);
            })
            .catch((error) => {
                console.error('Error fetching klant data', error);
                setIsLoading(false);
            });
    }, [id, token, fetchUserLoginData, normalizeCustomer]);

    useEffect(() => {
        fetchCustomer();
    }, [fetchCustomer]);

    const handleEmailBlur = async () => {
        if (!values.email) return;

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/auth/check-email-existence`,
                {
                    params: { email: values.email },
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            setEmailExists(response.data.exists && String(response.data.customer_id) !== String(id));
        } catch (error) {
            console.error('Error checking email existence:', error);
        }
    };

    const handleFeatureChange = (featureKey, checked) => {
        setValues((prev) => ({
            ...prev,
            features: {
                ...(prev.features || {}),
                [featureKey]: checked
            }
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (emailExists) {
            alert('Het e-mailadres bestaat al in het systeem.');
            return;
        }

        const cleanedValues = {
            ...values,
            is_single_user: Boolean(values.is_single_user),
            features: {
                ...(values.features || {}),
                private_lessons: values.features?.private_lessons === true
            }
        };

        try {
            setIsSaving(true);

            const res = await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/customer/updatecustomer/${id}`,
                cleanedValues,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (res.data.status === 'success') {
                navigate('/klanten');
            } else {
                alert('Error: ' + (res.data.status || res.data.error || 'Onbekende fout'));
            }
        } catch (error) {
            console.error('Error in updating customer:', error);
            alert(error?.response?.data?.error || 'Klant bijwerken mislukt.');
        } finally {
            setIsSaving(false);
        }
    };

    const checkAndDeactivateUser = async () => {
        try {
            const checkResponse = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/auth/check-email-in-users?email=${values.email}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (checkResponse.data.exists) {
                await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/auth/deactivate`,
                    { email: values.email },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                fetchUserLoginData(values.email);
            }
        } catch (error) {
            console.error('Error in checking email existence or deactivating user:', error);
        }
    };

    const checkAndActivateUser = async () => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/auth/activate`,
                {
                    name: values.name,
                    email: values.email,
                    role: 'super_admin',
                    form_customer_id: id
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            );

            if (response.data.Status === 'Success') {
                fetchUserLoginData(values.email);
            } else {
                alert('Mogelijk is er iets misgegaan');
            }
        } catch (error) {
            console.error('Error in registerOrActivateUser:', error);
        }
    };

    const handleCancel = () => {
        navigate('/klanten');
    };

    return (
        <div className='all-klant'>
            {isLoading ? (
                <div className='text-center'>Gegevens worden geladen...</div>
            ) : !values || Object.keys(values).length === 0 ? (
                <div className='text-center'>Geen klantgegevens gevonden.</div>
            ) : (
                <div className='bg-white p-4'>
                    <h2 className='text-center'>Klant Bewerken</h2>
                    <hr className='seperator' />
                    <form onSubmit={handleSubmit}>
                        <div className='row'>
                            <div className='mb-3 col'>
                                <button
                                    id="button_enable_login"
                                    type="button"
                                    className='btn btn-secondary w-50 rounded-0'
                                    onClick={checkAndActivateUser}
                                    disabled={isSaving}
                                >
                                    Enable inlog
                                </button>
                            </div>
                            <div className='mb-3 col'>
                                <button
                                    id="button_disable_login"
                                    type="button"
                                    className='btn btn-secondary w-50 rounded-0'
                                    onClick={checkAndDeactivateUser}
                                    disabled={isSaving}
                                >
                                    Disable inlog
                                </button>
                            </div>
                        </div>

                        <div className='mb-3 col'>
                            <p>Status: {userStatus ? userStatus : 'Loading...'}</p>
                            <p>Role: {userRole ? userRole : 'Loading...'}</p>
                        </div>

                        <div className='mb-3 col'>
                            <div className="form-check">
                                <input
                                    type='checkbox'
                                    id="checkbox_single_user"
                                    className='form-check-input'
                                    checked={values?.is_single_user || false}
                                    onChange={(e) =>
                                        setValues({ ...values, is_single_user: e.target.checked })
                                    }
                                />
                                <label className="form-check-label" htmlFor="checkbox_single_user">
                                    Is dit een single user?
                                </label>
                            </div>
                        </div>

                        <div className='mb-3'>
                            <label>Naam Klant *</label>
                            <input
                                type='text'
                                id='input_name'
                                className='form-control rounded-0'
                                required
                                value={values?.name || ''}
                                onChange={(e) => setValues({ ...values, name: e.target.value })}
                            />
                        </div>

                        <div className='row'>
                            <div className='mb-3 col'>
                                <label>Voornaam Contactpersoon</label>
                                <input
                                    type='text'
                                    id='input_contact_firstname'
                                    className='form-control rounded-0'
                                    value={values?.contact_firstname || ''}
                                    onChange={(e) =>
                                        setValues({ ...values, contact_firstname: e.target.value })
                                    }
                                />
                            </div>
                            <div className='mb-3 col'>
                                <label>Achternaam Contactpersoon</label>
                                <input
                                    type='text'
                                    id='input_contact_lastname'
                                    className='form-control rounded-0'
                                    value={values?.contact_lastname || ''}
                                    onChange={(e) =>
                                        setValues({ ...values, contact_lastname: e.target.value })
                                    }
                                />
                            </div>
                        </div>

                        <div className='row'>
                            <div className='mb-3 col'>
                                <label>Email *</label>
                                <input
                                    type='email'
                                    id="input_email"
                                    className='form-control rounded-0'
                                    required
                                    value={values?.email || ''}
                                    onChange={(e) => setValues({ ...values, email: e.target.value })}
                                    onBlur={handleEmailBlur}
                                    onFocus={() => setEmailExists(false)}
                                />
                                {emailExists && (
                                    <div style={{ color: 'red' }}>
                                        Dit e-mailadres is al in gebruik.
                                    </div>
                                )}
                            </div>
                            <div className='mb-3 col'>
                                <label>Mobiel Contactpersoon</label>
                                <input
                                    type='text'
                                    id="input_contact_mobile"
                                    className='form-control rounded-0'
                                    value={values?.contact_mobile || ''}
                                    onChange={(e) =>
                                        setValues({ ...values, contact_mobile: e.target.value })
                                    }
                                />
                            </div>
                        </div>

                        <div className='row'>
                            <div className='mb-3 col'>
                                <label>Telefoonnummer Klant</label>
                                <input
                                    type='text'
                                    id="input_phone"
                                    className='form-control rounded-0'
                                    value={values?.phone || ''}
                                    onChange={(e) => setValues({ ...values, phone: e.target.value })}
                                />
                            </div>
                            <div className='mb-3 col'>
                                <label>Volledig Adres</label>
                                <input
                                    type='text'
                                    id="input_address"
                                    className='form-control rounded-0'
                                    value={values?.address || ''}
                                    onChange={(e) => setValues({ ...values, address: e.target.value })}
                                />
                            </div>
                        </div>

                        <hr className='seperator' />

                        <h4 className="mb-3">Modules / functies</h4>
                        <div className="alert alert-light border">
                            Kies welke modules voor deze klant beschikbaar zijn. Deze instellingen kunnen gebruikt
                            worden om menu-items en backend toegang per klant aan of uit te zetten.
                        </div>

                        <div className="form-check mb-4">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="feature_private_lessons"
                                checked={values.features?.private_lessons === true}
                                onChange={(e) => handleFeatureChange('private_lessons', e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="feature_private_lessons">
                                Privélessen inschakelen
                            </label>
                            <div className="form-text">
                                Hiermee kan deze klant de privélesmodule gebruiken voor ouders, docenten en admins.
                            </div>
                        </div>

                        <hr className='seperator' />

                        <div className='row'>
                            <div className='col-md-4'>
                                <button
                                    type="button"
                                    id="button_cancel"
                                    className='btn btn-secondary w-100 rounded-0'
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                >
                                    Annuleren
                                </button>
                            </div>
                            <div className='col-md-4'>
                                <button
                                    id="button_save"
                                    type="submit"
                                    className='btn btn-secondary w-100 rounded-0'
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Opslaan...' : 'Opslaan'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

export default EditKlant;
